<script setup>
import { usePlaylistStore } from "@/stores/playlist.js";
const plStore = usePlaylistStore();

import { usePage } from '@inertiajs/vue3'
const page = usePage()

import { getBrowserInfo, isMobileDevice } from '@/Helpers/browser.js'
import { enableScroll, scrollToSlide } from '@/Helpers/scroll.js'
import { resizeViewport, handleOrientationChange } from '@/Helpers/resize.js'

import Popup from '@/Pages/Playlist/components/Misc/Popup.vue';

import Bugsnag from '@bugsnag/js';

import Timeline from '@/Pages/Playlist/components/Timeline.vue';
import Card from '@/Pages/Playlist/components/Card.vue';

import { Head } from '@inertiajs/vue3';

import { onMounted, ref } from 'vue'

const screenHeightTest = ref(document.documentElement.clientHeight);
const screenHeightTestCounter = ref(0);

setInterval(() => {
    screenHeightTest.value = document.documentElement.clientHeight;
    screenHeightTestCounter.value++;
}, 1000);

const props = defineProps({
    playlist: {
        type: Object,
        default: undefined,
    },
    auth: {
        type: Object,
        default: undefined,
    },
    is_guest: {
        type: Boolean,
        default: true
    },
    isPlaylistCompleted: {
        type: Boolean,
        default: false
    },
    previousAnswers: {
        type: Array,
        default: () => []
    },
    previousNudges: {
        type: Array,
        default: () => []
    },
    previousReflections: {
        type: Array,
        default: () => []
    },
    current_card: {
        type: Number,
        default: null
    },
    brand: {
        type: Object,
        default: undefined,
    },
    scormExport: {
        type: Boolean,
        default: false
    },
    certificatesUrl: {
        type: String,
        default: null,
    }
});

const showNavMsg = ref(false);
const showNavMsgMobile = ref(false);
const showScreenSizeMsg = ref(false);
const strNavMsg = ref('');

let startSwipeY;

plStore.s3BucketURL = page.props?.s3_bucket;
plStore.s3Region = page.props?.s3_region;
plStore.setBrand(props.brand);
plStore.setInitalCards(props.playlist?.cards);
plStore.setPlaylistId(props.playlist?.id);
plStore.setPlaylistTitle(props.playlist?.title);
plStore.setPlaylistCertificatesEnabled(props.playlist?.certificates_enabled);
plStore.setPlaylistCertificateIntervalDays(props.playlist?.certificate_interval_days);
plStore.setCertificatesUrl(props.certificatesUrl);
plStore.setPreviousAnswers(props.previousAnswers);
plStore.setPreviousNudges(props.previousNudges);
plStore.setPreviousReflections(props.previousReflections);
plStore.setIsGuest(props.is_guest);
plStore.setIsPlaylistComplete(props.isPlaylistCompleted);

onMounted(() => {
    //if (!props.is_guest) Bugsnag.setUser(props.auth.user.id, props.auth.user.email, props.auth.user.name)

    getBrowserInfo()
    isMobileDevice()

    window.addEventListener(
        "resize",
        handleWindowResize
    );

    // New code to slide to the current slide
    const progressCurrSlideIdx = plStore.cards.findIndex(item => item.id === props.current_card);

    if (progressCurrSlideIdx > 0 && progressCurrSlideIdx < plStore.cards.length - 1) {
        plStore.currentSlideIndex = progressCurrSlideIdx;
        scrollToSlide(progressCurrSlideIdx);
    }

    const mobContainer = document.querySelector('#mobileContainer');

    handleWindowResize();

    if (!plStore.isMobileDevice) {

        window.addEventListener('keydown', handleKeyPress, false);

        mobContainer.addEventListener('touchstart', function (e) {
            startSwipeY = e.touches[0].clientY;
        }, { passive: true });

        mobContainer.addEventListener('touchend', function (e) {
            // Ensure there was a touchstart event
            if (startSwipeY !== undefined) {

                // Calculate the distance swiped
                let endY = e.changedTouches[0].clientY;
                let distanceY = startSwipeY - endY;

                // Determine swipe direction and trigger corresponding event
                if (Math.abs(distanceY) > 50) { // Threshold to determine swipe
                    if (distanceY > 0) {
                        scrollNext(true);
                        handleWindowResize();
                    } else {
                        scrollPrev(true);
                        handleWindowResize();
                    }
                }

                // Reset startSwipeY
                startSwipeY = undefined;
            }
        }, { passive: true });
    }
})

function handleKeyPress(event) {
    if (event.keyCode == 38) {
        if (event.srcElement.localName !== "input" && event.srcElement.localName !== "textarea") scrollPrev(false);
    }
    if (event.keyCode == 40) {
        if (event.srcElement.localName !== "input" && event.srcElement.localName !== "textarea") scrollNext(false);
    }
}

function handleWindowResize() {
    resizeViewport();
    handleOrientationChange();
}

function scrollNext() {
    showNavMsg.value = false;
    if (plStore.currentSlideIndex == plStore.cards.length - 1) return;
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'Slider') {
        showScrollMsg('Please submit your answer before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'Confidence') {
        showScrollMsg('Please submit your answer before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'ConfidenceConfirmation') {
        showScrollMsg('Please submit your answer before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'Video') {
        showScrollMsg('Please finish watching the video before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'Nudges') {
        showScrollMsg('Please review the nudges and click the button before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'TextInput') {
        showScrollMsg('Please enter a response before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'Reflection') {
        showScrollMsg('Please enter a response before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'MultiPageForm') {
        showScrollMsg('Please complete the form before continuing.');
        return;
    }
    if (plStore.scrollDisabled) {
        for (let i = 0; i < plStore.cards[plStore.currentSlideIndex].rows.length; i++) {
            if (plStore.cards[plStore.currentSlideIndex].rows[i].type == 'Quiz') {
                showScrollMsg('Please answer the question before continuing.');
                return;
            }
            if (plStore.cards[plStore.currentSlideIndex].rows[i].type == 'TextInput') {
                showScrollMsg('Please enter a response before continuing.');
                return;
            }
            if (plStore.cards[plStore.currentSlideIndex].rows[i].type == 'Reflection') {
                showScrollMsg('Please enter a response before continuing.');
                return;
            }
        }
    }
    enableScroll();
    if (plStore.currentSlideIndex < (plStore.cards.length -1)) scrollToSlide(plStore.currentSlideIndex + 1);
}

function scrollPrev() {
    showNavMsg.value = false;
    if (plStore.currentSlideIndex == 0) return;
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'Slider') {
        showScrollMsg('Please submit your answer before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'Confidence') {
        showScrollMsg('Please submit your answer before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'ConfidenceConfirmation') {
        showScrollMsg('Please submit your answer before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'Video') {
        showScrollMsg('Please finish watching the video before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'Nudges') {
        showScrollMsg('Please review the nudges and click the button before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'TextInput') {
        showScrollMsg('Please enter a response before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'Reflection') {
        showScrollMsg('Please enter a response before continuing.');
        return;
    }
    if (plStore.scrollDisabled && plStore.cards[plStore.currentSlideIndex].rows[0].type == 'MultiPageForm') {
        showScrollMsg('Please complete the form before continuing.');
        return;
    }
    if (plStore.scrollDisabled) {
        for (let i = 0; i < plStore.cards[plStore.currentSlideIndex].rows.length; i++) {
            if (plStore.cards[plStore.currentSlideIndex].rows[i].type == 'Quiz') {
                showScrollMsg('Please answer the question before continuing.');
                return;
            }
            if (plStore.cards[plStore.currentSlideIndex].rows[i].type == 'TextInput') {
                showScrollMsg('Please enter a response before continuing.');
                return;
            }
            if (plStore.cards[plStore.currentSlideIndex].rows[i].type == 'Reflection') {
                showScrollMsg('Please enter a response before continuing.');
                return;
            }
        }
    }
    enableScroll();
    if (plStore.currentSlideIndex > 0) scrollToSlide(plStore.currentSlideIndex - 1);
}

function showScrollMsg(strMsg) {
    strNavMsg.value = strMsg;
    if (!plStore.isMobileDevice) showNavMsg.value = true;
    if (plStore.isMobileDevice) {
        showNavMsgMobile.value = true;
        setTimeout(() => {
            const slide = document.querySelector("[data-slide-index='" + plStore.currentSlideIndex + "']")
            const navMsg = document.getElementById('navMsgMobile');
            navMsg.style.top = slide.offsetTop + 'px';
        }, 100);
    }
    setTimeout(() => {
        fadeOutNavMsg();
    }, 1000);
}

function fadeOutNavMsg() {
    let intOpacity = 1;
    let elem;
    if (!plStore.isMobileDevice) elem = document.getElementById('navMsg');
    if (plStore.isMobileDevice) elem = document.getElementById('navMsgMobile');
    if (!elem) return;
    elem.style.opacity = 1;
    let opactityTimer = setInterval(() => {
        if (intOpacity <= 0) {
            elem.style.opacity = 0;
            showNavMsg.value = false;
            showNavMsgMobile.value = false;
            intOpacity = 1;
            clearInterval(opactityTimer);
            return;
        }
        intOpacity -= 0.2;
        elem.style.opacity = intOpacity;
    }, 200);
}

</script>

<template>
    <Head :title="plStore.playlistTitle" />

    <main
        :class="[plStore.orientation, { 'fullscreen-disabled': !plStore.isShowingFullscreenVideo }]" 
        :style="'--brand-colour: ' + brand.colour + '; --brand-colour-secondary: ' + (brand.colour_secondary || brand.colour) + '; --brand-colour-tertiary: ' + (brand.colour_tertiary || brand.colour) + ';'"
    >
        <!-- Start of playlist layout -->
        <div class="platform-bg playlist-layout-container flex flex-row h-screen-comp overflow-hidden w-full relative">
            <div v-if="1 === 2" class="absolute bottom-0 left-0 w-full h-20 text-center bg-white/80 pt-2 z-50 text-lg flex items-center justify-center">
                SH: {{ plStore.screenHeight }} || SH2: {{ screenHeightTest }} || TST: {{ plStore.targetScrollTop }} || C: {{ screenHeightTestCounter }}
            </div>
            <!-- start of playlist panel -->
            <div class="grow h-screen-comp">
                <div id="playlistPanel" class="max-w-lg aspect-9/16 mx-auto relative w-screen h-screen-comp">
                    <div
                        class="absolute top-3 right-3 z-40 rounded-full bg-white/70 backdrop-blur-sm shadow-sm px-2.5 py-1 text-xs font-medium text-platform-black/80 tabular-nums select-none"
                    >
                        {{ plStore.currentSlideIndex + 1 }} / {{ plStore.cards.length }}
                    </div>
                    <div
                        id="mobileContainer"
                        class="h-screen-comp relative pt-3 text-platform-black sm:shadow-lg w-full scroll-smooth"
                        :class="[
                            { 'overflow-hidden': !plStore.isMobileDevice },
                            { 'overflow-scroll snap-y snap-mandatory': plStore.isMobileDevice }
                        ]"
                    >
                        <section
                            v-if="showNavMsgMobile"
                            id="navMsgMobile"
                            class="absolute text-center text-platform-black text-xl w-full bg-platform-orange-light rounded-lg px-4 py-2 border border-(--brand-colour) left-0 z-50"
                            v-text="strNavMsg"
                        />
                        <section
                            v-if="showScreenSizeMsg"
                            id="screenSizeMsg"
                            class="absolute text-center text-platform-black text-xl w-full bg-platform-orange-light rounded-lg px-4 py-2 border border-(--brand-colour) left-0 z-50"
                        >
                            Your screen size is too small for the platform to work optimally, we
                            advise maximising your window or trying another device.
                        </section>
                        <section
                            v-for="(card, i) in playlist.cards"
                            :key="card.id"
                            class="playlist-slide relative h-screen-comp bg-white w-full"
                            :class="[
                                { 'snap-always snap-center': plStore.isMobileDevice },
                            ]"
                            :data-slide-index="i"
                        >
                            <Card :index="i" :card-id="card.id" :playlist-id="props.playlist.id" :brand="brand" :scorm-export="scormExport" />
                        </section>
                    </div>
                </div>
            </div>
            <!-- End of playlist panel -->

            <!-- Start of nav panel -->
            <div
                class="hidden sm:inline-flex h-full flex flex-col gap-10 justify-center items-center bg-transparent mr-5 relative"
            >
                <section
                    class="rounded-full text-white border "
                    :class="[
                        { 'bg-platform-gray-light border-platform-gray-light': plStore.currentSlideIndex == 0 },
                        { 'border-(--brand-colour) bg-(--brand-colour) transition-all hover:text-(--brand-colour) hover:bg-platform-orange-light cursor-pointer': plStore.currentSlideIndex > 0 }
                    ]"
                    @click="scrollPrev(false)"
                >
                    <font-awesome-icon icon="fa-light fa-caret-up" class="h-8 w-8 p-2" size="2xl" />
                </section>
                <section
                    class="rounded-full text-white border "
                    :class="[
                        { 'bg-platform-gray-light border-platform-gray-light': plStore.currentSlideIndex == playlist.cards.length - 1 },
                        { 'border-(--brand-colour) bg-(--brand-colour) transition-all hover:text-(--brand-colour) hover:bg-platform-orange-light cursor-pointer': plStore.currentSlideIndex < (playlist.cards.length - 1) }
                    ]"
                    @click="scrollNext(false)"
                >
                    <font-awesome-icon icon="fa-light fa-caret-down" class="h-8 w-8 p-2" size="2xl" />
                </section>
                <section
                    v-if="showNavMsg"
                    id="navMsg"
                    class="absolute text-center text-platform-black text-base bg-platform-orange-light rounded-lg px-4 py-2 border border-(--brand-colour) right-0 z-40"
                    v-text="strNavMsg"
                />
            </div>
            <!-- End of nav panel -->

            <!-- Start of timeline panel -->
            <div class="bg-white overflow-hidden lg:block w-2/6 xl:w3/6 px-12 pt-2 pb-1 xl:px-18 xl:pt-4 xl:py-2 flex flex-col">
                <section class="h-full w-full flex flex-col">
                    <section class="font-bold text-4xl text-platform-black mb-6">
                        {{ plStore.playlistTitle }}
                    </section>
                    <section class="grow overflow-scroll timelineContainer">
                        <Timeline :scorm-export="scormExport" />
                    </section>
                </section>
            </div>
            <!-- End of timeline panel -->
        </div>
        <!-- End of playlist layout -->

        <!-- Start of small mobile layout -->
        <div class="small-mobile-container flex flex-col text-center" :class="plStore.orientation + ' ' + (plStore.isShowingFullscreenVideo ? 'fullscreen' : '')">
            <div class="h-12 m-4">
                <img
                    :src="'https://' + plStore.s3BucketURL + '.s3.' + plStore.s3Region + '.amazonaws.com/platform/logo.svg'"
                    alt="Logo"
                    class="h-full m-auto"
                >
            </div>
            <h2 class="font-black text-5xl m-4">
                Oops
            </h2>
            <p class="text-xl m-4">
                Your device is too small for the platform to work correctly, please try another device.
            </p>
        </div>
        <!-- End of small mobile layout -->
    </main>

    <Popup />
</template>

<style>
.playlist-slide {
    overflow: hidden;
}

.h-screen-comp {
    height: 100vh;
    height: calc(var(--vh, 1vh) * 100);
}

.platform-bg {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' version='1.1' xmlns:xlink='http://www.w3.org/1999/xlink' xmlns:svgjs='http://svgjs.dev/svgjs' width='1440' height='1440' preserveAspectRatio='none' viewBox='0 0 1440 1440'%3e%3cg mask='url(%26quot%3b%23SvgjsMask1009%26quot%3b)' fill='none'%3e%3cpath d='M106.59 1669.04C274.57 1616.62 318.72 1140.14 563.93 1133.99 809.15 1127.84 792.6 1313.99 1021.28 1313.99 1249.95 1313.99 1355.79 1137.19 1478.62 1133.99' stroke='rgba(229%2c 94%2c 44%2c 1)' stroke-width='2'%3e%3c/path%3e%3cpath d='M589.23 1593.26C726.59 1575.37 708.31 1359.18 1032.01 1260.18 1355.71 1161.18 1291 660.28 1474.79 568.98' stroke='rgba(229%2c 94%2c 44%2c 1)' stroke-width='2'%3e%3c/path%3e%3cpath d='M630.7 1629.43C886.89 1383.17 844.21 329.2 1145.41 304.47 1446.62 279.74 1459.64 916.79 1660.13 995.67' stroke='rgba(229%2c 94%2c 44%2c 1)' stroke-width='2'%3e%3c/path%3e%3cpath d='M280.07 1569.31C475.47 1444.87 449.88 785.47 718.96 753.21 988.04 720.95 938.41 933.21 1157.85 933.21 1377.3 933.21 1478.2 756.66 1596.74 753.21' stroke='rgba(229%2c 94%2c 44%2c 1)' stroke-width='2'%3e%3c/path%3e%3cpath d='M351.77 1505.22C537.84 1371.25 500.18 719.49 755.55 681.79 1010.92 644.09 957.45 861.79 1159.34 861.79 1361.23 861.79 1452.67 685.8 1563.12 681.79' stroke='rgba(229%2c 94%2c 44%2c 1)' stroke-width='2'%3e%3c/path%3e%3c/g%3e%3cdefs%3e%3cmask id='SvgjsMask1009'%3e%3crect width='1440' height='1440' fill='white'%3e%3c/rect%3e%3c/mask%3e%3c/defs%3e%3c/svg%3e");
    background-size: cover;
}

@media screen and (min-height: 499px) {
    .playlist-layout-container {
        display: flex-inline;
    }

    .landscape-mobile-container,
    .small-mobile-container {
        display: none;
    }
}

@media screen and (max-height: 500px) {
    main.fullscreen-disabled .playlist-layout-container {
        display: none;
    }

    main.fullscreen-disabled.landscape-mobile-container {
        display: none;
    }

    main.fullscreen-disabled.small-mobile-container {
        display: block;
    }

    main.landscape-primary .playlist-layout-container {
        display: none;
    }

    .landscape-primary.landscape-mobile-container {
        display: block;
    }

    .landscape-primary.small-mobile-container {
        display: none;
    }
}

/* Add any other styles specific to this condition */
</style>